const API_BASE = process.env.VUE_APP_API_BASE;
const API_URL = `${API_BASE}/ajax/files.php`

let activeFilesController = null

const createFilesController = () => {
  if (activeFilesController) {
    activeFilesController.abort()
  }

  activeFilesController = new AbortController()
  return activeFilesController
}

const releaseFilesController = (controller) => {
  if (activeFilesController === controller) {
    activeFilesController = null
  }
}

export default {
  namespaced: true,

  state: () => ({
    files: [],
    folders: [],
    loading: false,
    error: null,
  }),

  getters: {
    getFiles: (state) => state.files,
    getVigentes: (state) => state.files.filter(f => f.vigente === 1),
  },

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_FILES(state, files) {
      state.files = files
    },
    CLEAR_FILES(state) {
      state.files = []
      state.folders = []
      state.loading = false
      state.error = null
    },
    SET_FOLDERS(state, folders) {
      state.folders = folders
    }
  },

  actions: {
    cancelRequests() {
      if (activeFilesController) {
        activeFilesController.abort()
        activeFilesController = null
      }
    },

    /* 📁 Obtener todos los archivos (admin) */
    async fetchFiles({ commit }) {
      const controller = createFilesController()
      try {
        const res = await fetch(`${API_URL}?action=getFiles`, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        })
        const data = await res.json()
        if (data.success) {
          commit("SET_FILES", data.data)
        } else {
          console.error("Error obteniendo archivos:", data.message)
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetchFiles:", err)
        }
      } finally {
        releaseFilesController(controller)
      }
    },

    /* 📥 Subir archivo */
    async uploadFile({ dispatch }, { archivo, id_folder = null }) {
      try {
        const formData = new FormData()
        formData.append("archivo", archivo)

        if (id_folder !== null && id_folder !== undefined) {
          formData.append("id_folder", id_folder)
        }

        const res = await fetch(`${API_URL}?action=uploadFile`, {
          method: "POST",
          credentials: "include",
          body: formData,
        })

        const data = await res.json()

        if (data.success) {
          await dispatch("fetchFiles")
          await dispatch("fetchFolders")
        }

        return data
      } catch (err) {
        console.error("Error uploadFile:", err)
        return { success: false, message: "Error al subir archivo" }
      }
    },

    /* ♻️ Cambiar vigencia (toggle) */
    async toggleVigente({ dispatch }, { id, vigente }) {
      try {
        const res = await fetch(`${API_URL}?action=setVigente`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, vigente }),
        })
        const data = await res.json()
        if (data.success) {
          await dispatch("fetchFiles")
        }
        return data
      } catch (err) {
        console.error("Error toggleVigente:", err)
        return { success: false }
      }
    },

    /* 🗑️ Eliminar archivo */
    async deleteFile({ dispatch }, id_file) {
      try {
        const formData = new FormData()
        formData.append("id", id_file)

        const res = await fetch(`${API_URL}?action=deleteFile`, {
          method: "POST",
          body: formData,
        })

        const data = await res.json()
        if (data.success) {
          await dispatch("fetchFiles")
        }
        return data
      } catch (err) {
        console.error("Error deleteFile:", err)
        return { success: false }
      }
    },

    /* 👁️ Obtener solo vigentes (usuarios finales) */
    async fetchFilesVigentes({ commit }) {
      const controller = createFilesController()
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      // La tabla del cliente siempre parte vacía para no mostrar datos de una
      // sesión o respuesta anterior mientras se obtiene información actualizada.
      commit("SET_FILES", [])
      try {
        const params = new URLSearchParams({
          action: "getFilesVigentes",
          _: Date.now().toString(),
        })
        const res = await fetch(`${API_URL}?${params.toString()}`, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        })
        const data = await res.json()

        if (!res.ok || !data.success) {
          const message = data.message || "No se pudieron cargar los documentos"
          commit("SET_FILES", [])
          commit("SET_ERROR", message)
          return { success: false, status: res.status, message }
        }

        const files = Array.isArray(data.data) ? data.data.filter(Boolean) : []
        commit("SET_FILES", files)
        return { success: true, data: files }
      } catch (err) {
        if (err.name === "AbortError") {
          return { success: false, aborted: true }
        }
        console.error("Error fetchFilesVigentes:", err)
        const message = "Error de conexión al cargar los documentos"
        commit("SET_FILES", [])
        commit("SET_ERROR", message)
        return { success: false, message }
      } finally {
        releaseFilesController(controller)
        if (!activeFilesController) {
          commit("SET_LOADING", false)
        }
      }
    },

    /* Obtener las Carpetas */
    async fetchFolders({ commit }) {
      try {
        const res = await fetch(`${API_URL}?action=getFolders`)
        const data = await res.json()
        if (data.success) {
          commit("SET_FOLDERS", data.data)
        } else {
          console.error("Error obteniendo carpetas:", data.message)
        }
      } catch (err) {
        console.error("Error fetchFolders:", err)
      }
    },

    /* Añadir Carpeta */
    async AddFolders({ commit, dispatch }, folderData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_URL}?action=addFolder`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(folderData),
        })
        const data = await response.json()

        if (data.success) {
          await Promise.all([
            dispatch("fetchFolders"),
            dispatch("fetchFiles"),
          ])
        }
        return data
      } catch (err) {
        console.error("Error Folder:", err)
        return { success: false, message: "Error al crear la Carpeta" }
      }
    },

    /* Eliminar una carpeta vacía */
    async deleteFolder({ dispatch }, id_folder) {
      try {
        const response = await fetch(`${API_URL}?action=deleteFolder`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id_folder }),
        })
        const data = await response.json()

        if (data.success) {
          await Promise.all([
            dispatch("fetchFolders"),
            dispatch("fetchFiles"),
          ])
        }

        return data
      } catch (err) {
        console.error("Error deleteFolder:", err)
        return { success: false, error: "Error al eliminar la carpeta" }
      }
    },

    /** Añadir permisos sobre una carpeta */
    async addPermisosFolder({ dispatch }, { id_folder, usuarios }) {
      try {
        const formData = new FormData()
        formData.append("id_folder", id_folder)
        usuarios.forEach(usuario => {
          formData.append("usuarios[]", usuario)
        })

        const res = await fetch(`${API_URL}?action=addPermisionFolder`, {
          method: "POST",
          credentials: "include",
          body: formData,
        })

        const data = await res.json()
        if(data.success) {
          await dispatch("fetchFiles")
        }

        return data
      } catch (err) {
        console.log("Error:", err)
        return { success: false }
      }
    },

    /** Ver los permisos sobre una carpeta */
    async getPermisionFolder(_, payload) {
      const params = new URLSearchParams({
        action: "getPermitionFolder",
        id_folder: payload.id_folder
      })

      const response = await fetch(`${API_URL}?${params.toString()}`, {
        method: "GET",
        credentials: "include"
      })

      return await response.json()
    },

    /** Eliminar los permisos sobre una carpeta */
    async deletePermisionFolder(_, payload) {
      const formData = new FormData()
      formData.append("id_permisos_folder", payload.id_permisos_folder)

      const response = await fetch(`${API_URL}?action=deletePermisionFolder`, {
        method: "POST",
        credentials: "include",
        body: formData
      })

      return await response.json()
    },

    /** Añadir permisos sobre un archivo*/
    async addPermisosFile({ dispatch }, { id_file, usuarios }) {
      try {
        const formData = new FormData()
        formData.append("id_file", id_file)
        usuarios.forEach(usuario => {
          formData.append("usuarios[]", usuario)
        })

        const res = await fetch(`${API_URL}?action=addPermisionFile`, {
          method: "POST",
          credentials: "include",
          body: formData,
        })

        const data = await res.json()
        if(data.success) {
          await dispatch("fetchFiles")
        }

        return data
      } catch (err) {
        console.log("Error:", err)
        return { success: false }
      }
    },

    /** Ver los permisos sobre un archivo */
    async getPermisionFile(_, payload) {
      const params = new URLSearchParams({
        action: "getPermitionFile",
        id_file: payload.id_file
      })

      const response = await fetch(`${API_URL}?${params.toString()}`, {
        method: "GET",
        credentials: "include"
      })

      return await response.json()
    },

    /**Eliminar los permisos sobre un archivo */
    async deletePermisionFile(_, payload) {
      const formData = new FormData()
      formData.append("id_permisos_file", payload.id_permisos_file)

      const response = await fetch(`${API_URL}?action=deletePermisionFile`, {
        method: "POST",
        credentials: "include",
        body: formData
      })

      return await response.json()
    }
  },
}
